/*
 * QuestionarioBlocker — trava o app do funcionário para o fluxo completo
 * do Questionário Psicossocial: 1) ASSINA a lista de presença via OTP
 * (obrigatório); 2) responde o questionário (anônimo — só o posto
 * acompanha). O blocker só libera quando as DUAS etapas terminam, sem
 * precisar atualizar a página.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Loader2, ClipboardList, ShieldCheck, FileSignature, PenLine,
} from "lucide-react";
import { AttendanceSignDialog } from "./EmployeeAttendanceConvocations";

const SCALE_COLORS = [
  "bg-red-100 border-red-300 text-red-800",
  "bg-orange-100 border-orange-300 text-orange-800",
  "bg-amber-100 border-amber-300 text-amber-800",
  "bg-lime-100 border-lime-300 text-lime-800",
  "bg-emerald-100 border-emerald-300 text-emerald-800",
];

export default function QuestionarioBlocker() {
  const { token } = useAuth();
  const [pending, setPending] = useState([]);      // questionários a responder
  const [signPending, setSignPending] = useState([]); // listas a assinar
  const [catalog, setCatalog] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [signOpen, setSignOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [attR, qR] = await Promise.all([
        axios.get(`${API}/employee/pending-attendances`, {
          params: { token }, timeout: 15000,
        }),
        axios.get(`${API}/employee/questionario/pending`, {
          params: { token }, timeout: 15000,
        }),
      ]);
      const signs = (attR.data?.items || []).filter(
        (i) => i.course_key === "questionario_psicossocial",
      );
      const answersPending = qR.data?.items || [];
      setSignPending(signs);
      setPending(answersPending);
      if ((signs.length || answersPending.length) && !catalog) {
        const c = await axios.get(`${API}/questionario/catalog`);
        setCatalog(c.data);
      }
      setLoading(false);
    } catch {
      // NÃO "fail open": mantém o estado atual e tenta de novo — o
      // questionário é obrigatório e não pode sumir por falha de rede.
      setTimeout(load, 5000);
    }
  }, [token, catalog]);

  useEffect(() => { load(); }, [load]);

  if (loading || (pending.length === 0 && signPending.length === 0)) {
    return null;
  }

  // ── Etapa 1: assinar a lista de presença (OTP) ──
  if (signPending.length > 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
        data-testid="questionario-sign-blocker"
      >
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <FileSignature className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-lg">
                Questionário Psicossocial
              </h2>
              <p className="text-xs text-slate-500">
                Etapa 1 de 2 — assinatura da lista de presença
              </p>
            </div>
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            Para liberar o acesso ao painel você precisa{" "}
            <strong>assinar a lista de presença</strong> (código via SMS ou
            e-mail) e, em seguida, <strong>responder o questionário</strong>{" "}
            — as respostas são anônimas.
          </div>
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11"
            onClick={() => setSignOpen(true)}
            data-testid="questionario-sign-now-btn"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Assinar agora
          </Button>
        </div>

        {signOpen && (
          <AttendanceSignDialog
            tokenId={signPending[0].token}
            onClose={() => setSignOpen(false)}
            onSigned={() => {
              setSignOpen(false);
              // Sem refresh: recarrega e cai direto na etapa 2 (perguntas).
              window.dispatchEvent(new Event("avi-attendances-refresh"));
              load();
            }}
          />
        )}
      </motion.div>
    );
  }

  // ── Etapa 2: responder o questionário ──
  if (!catalog) return null;

  const current = pending[0];
  const total = catalog.total_questions;
  const answered = Object.keys(answers).length;

  const submit = async () => {
    if (answered < total) {
      toast.error(`Responda todas as perguntas (${answered}/${total})`);
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/employee/questionario/submit`,
        { list_id: current.list_id, answers },
        { params: { token } },
      );
      toast.success("Questionário enviado! Obrigado pela participação.");
      setAnswers({});
      await load();
      setSending(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar as respostas");
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      data-testid="questionario-blocker">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[94vh] flex flex-col overflow-hidden">
        <div className="bg-slate-900 px-5 py-4 shrink-0">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-amber-400 shrink-0" />
            <div>
              <h2 className="text-white font-bold">Questionário Psicossocial</h2>
              <p className="text-slate-400 text-xs">
                Etapa 2 de 2 · respostas anônimas · escala 1-Sempre a 5-Nunca
                · seu setor é o seu posto de trabalho (automático)
              </p>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${(answered / total) * 100}%` }}
            />
          </div>
          <p className="text-[11px] text-slate-400 mt-1" data-testid="questionario-progress">
            {answered}/{total} respondidas
          </p>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-6 flex-1">
          {catalog.dimensions.map((dim) => (
            <div key={dim.id} className="space-y-3">
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                <p className="text-xs font-bold text-amber-900">
                  {dim.dimensao} — {dim.fator}
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5">{dim.descricao}</p>
              </div>
              {dim.questions.map((q) => (
                <div key={q.id} className="space-y-1.5" data-testid={`questionario-q-${q.id}`}>
                  <p className="text-sm font-medium text-slate-800">{q.texto}</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {catalog.scale.map((label, idx) => {
                      const val = idx + 1;
                      const selected = answers[q.id] === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() =>
                            setAnswers((a) => ({ ...a, [q.id]: val }))}
                          data-testid={`questionario-${q.id}-opt-${val}`}
                          className={`border rounded-md px-1 py-1.5 text-[10px] leading-tight font-medium transition-colors ${
                            selected
                              ? `${SCALE_COLORS[idx]} ring-2 ring-slate-900/20`
                              : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 shrink-0 bg-white">
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11"
            onClick={submit}
            disabled={sending || answered < total}
            data-testid="questionario-submit-btn"
          >
            {sending
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <ShieldCheck className="w-4 h-4 mr-2" />}
            Enviar respostas ({answered}/{total})
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
